import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Volume2, Pause } from "lucide-react";
import { fetchSermon } from "../lib/api";
import { trackSermon } from "../lib/readingProgress";
import { Skeleton } from "../components/ui/skeleton";
import useTTS from "../lib/useTTS";
import ShareButton from "../features/share/ShareButton";

const Block = ({ label, children, index }) => {
  const text = typeof children === "string" ? children.trim() : children;
  if (!text) return null;
  return (
    <section className="mt-10 fade-in" style={{ animationDelay: `${index * 0.08}s` }}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</span>
        <span className="flex-1 h-px bg-border" />
      </div>
      <div className="text-navy/90 leading-relaxed text-base sm:text-[17px] whitespace-pre-wrap">
        {text}
      </div>
    </section>
  );
};

export default function SermaoDetalhe() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSermon(id)
      .then((data) => {
        setS(data);
        if (data?.title) trackSermon({ id: data.id || id, title: data.title });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const tts = useTTS();
  const onTTSClick = () => {
    if (!s) return;
    if (tts.playing && !tts.paused) { tts.pause(); return; }
    if (tts.playing && tts.paused) { tts.resume(); return; }
    const items = [];
    if (s.title) items.push({ id: "title", text: s.title });
    if (s.base_text) items.push({ id: "base", text: `Texto base. ${s.base_text}` });
    if (s.theme) items.push({ id: "theme", text: s.theme });
    if (s.introduction) items.push({ id: "intro", text: s.introduction });
    if (s.development) items.push({ id: "dev", text: s.development });
    if (s.application) items.push({ id: "app", text: s.application });
    if (s.conclusion) items.push({ id: "concl", text: s.conclusion });
    tts.speak(items);
  };

  const sermonMainPhrase =
    (s?.theme && s.theme.trim()) ||
    (s?.introduction && s.introduction.trim().slice(0, 220)) ||
    (s?.application && s.application.trim().slice(0, 220)) ||
    "";

  const isBriefStudy = s?.content_type === "brief_study";
  const kindLabel = isBriefStudy ? "Estudo Breve" : "Sermão";
  const kindPathLabel = isBriefStudy ? "Estudos Breves" : "Sermões";
  const backTo = isBriefStudy ? "/estudos-breves" : "/sermoes";
  const readingMinutes = s?.reading_minutes || 0;

  return (
    <div className="max-w-5xl mx-auto section-padding py-8" data-testid="sermon-detail-page" data-content-type={s?.content_type || "sermon"}>
      <Link
        to={backTo}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="sermon-back"
      >
        <ArrowLeft className="w-4 h-4" /> {kindPathLabel}
      </Link>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : s ? (
        <article className="reading-max mx-auto">
          <div className="flex items-start justify-between gap-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50" data-testid="sermon-category">
              {s.category}
            </p>
            <div className="flex items-center gap-1 shrink-0" data-testid="sermon-actions">
              {tts.supported && (
                <button
                  type="button"
                  onClick={onTTSClick}
                  aria-label={tts.playing && !tts.paused ? "Pausar" : "Ouvir sermão"}
                  data-testid="sermon-tts"
                  data-state={tts.playing ? (tts.paused ? "paused" : "playing") : "idle"}
                  className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
                    tts.playing
                      ? "bg-gold border-gold text-navy"
                      : "bg-white border-border text-navy/60 hover:text-gold"
                  }`}
                >
                  {tts.playing && !tts.paused ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              )}
              <div data-testid="sermon-share-slot">
                <ShareButton
                  contentType="sermon"
                  contentId={s.id || id}
                  title={s.title}
                  mainText={sermonMainPhrase}
                  verse={s.base_text}
                  verseRef={s.base_text}
                  theme={s.category || ""}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 flex-wrap" data-testid="sermon-kind-row">
            <span
              data-testid="sermon-kind-badge"
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-medium ${
                isBriefStudy
                  ? "bg-gold/15 text-gold border border-gold/40"
                  : "bg-navy/10 text-navy border border-navy/20"
              }`}
            >
              {kindLabel}
            </span>
            {readingMinutes > 0 && (
              <span
                data-testid="sermon-reading-time"
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest text-navy/55 border border-border"
              >
                {readingMinutes} min de leitura
              </span>
            )}
          </div>

          <h1 className="font-display text-4xl sm:text-5xl text-navy mt-3 leading-[1.08]" data-testid="sermon-title">
            {s.title}
          </h1>
          {s.base_text && (
            <p className="mt-3 text-gold text-sm tracking-wide" data-testid="sermon-base-text">
              Texto base · {s.base_text}
            </p>
          )}

          <Block label="Tema" index={0}>{s.theme}</Block>
          <Block label="Introdução" index={1}>{s.introduction}</Block>
          <Block label="Desenvolvimento" index={2}>{s.development}</Block>
          <Block label="Aplicação" index={3}>{s.application}</Block>
          <Block label="Conclusão" index={4}>{s.conclusion}</Block>

          {s.source_url && (
            <div className="mt-12 pt-8 border-t border-border">
              <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50 mb-3">Leitura completa</p>
              <a
                href={s.source_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sermon-source-link"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-navy text-white px-5 py-2.5 rounded-full hover:bg-gold hover:text-navy transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Ler sermão na íntegra
              </a>
            </div>
          )}
        </article>
      ) : (
        <p className="text-navy/60">Sermão não encontrado.</p>
      )}
    </div>
  );
}
